import React, { useState } from "react";
import {
  Card,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Switch,
  Pagination,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import toast from "react-hot-toast";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  TrackChanges,
  Bolt,
  EmojiEvents,
  Stars,
  CheckCircle,
  MonetizationOn,
  TrendingUp,
} from "@mui/icons-material";
import {
  useGetMissionsAdminQuery,
  useCreateMissionMutation,
  useUpdateMissionMutation,
  useToggleMissionStatusMutation,
  useDeleteMissionMutation,
} from "lib/store/service/mission";

function MissionsManagement() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useGetMissionsAdminQuery({ page, limit: 10 });

  const [createMutation, { isLoading: isCreating }] = useCreateMissionMutation();
  const [updateMutation, { isLoading: isUpdating }] = useUpdateMissionMutation();
  const [toggleStatusMutation] = useToggleMissionStatusMutation();
  const [deleteMutation] = useDeleteMissionMutation();

  // Modals state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    target: 3,
    rewardCoins: 10,
    icon: "Target",
    route: "/classic_mode",
    actionLabel: "Play Classic Mode",
    isActive: true,
  });

  const rawMissions = data?.data?.missions || data?.response?.data?.missions || data?.data || [];
  const totalMissions = data?.data?.total || data?.response?.data?.total || rawMissions.length;
  const totalPages = data?.data?.totalPages || data?.response?.data?.totalPages || 1;

  const activeCount = rawMissions.filter((m) => m.isActive).length;
  const hiddenCount = rawMissions.filter((m) => !m.isActive).length;
  const totalClaims = rawMissions.reduce((acc, curr) => acc + (curr.totalClaimsCount || 0), 0);

  const handleOpenCreate = () => {
    setFormData({
      title: "",
      description: "",
      target: 3,
      rewardCoins: 10,
      icon: "Target",
      route: "/classic_mode",
      actionLabel: "Play Classic Mode",
      isActive: true,
    });
    setCreateModalOpen(true);
  };

  const handleOpenEdit = (mission) => {
    setSelectedMission(mission);
    setFormData({
      title: mission.title || "",
      description: mission.description || "",
      target: mission.target || 1,
      rewardCoins: mission.rewardCoins || 10,
      icon: mission.icon || "Target",
      route: mission.route || "/classic_mode",
      actionLabel: mission.actionLabel || "Play Classic Mode",
      isActive: mission.isActive !== undefined ? mission.isActive : true,
    });
    setEditModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!formData.title || !formData.description) {
      return toast.error("Please fill in mission title and description");
    }
    try {
      await createMutation(formData).unwrap();
      toast.success("✅ New Daily Mission created successfully!");
      setCreateModalOpen(false);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create mission");
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedMission?._id) return;
    try {
      await updateMutation({ id: selectedMission._id, ...formData }).unwrap();
      toast.success("✅ Mission updated successfully!");
      setEditModalOpen(false);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update mission");
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleStatusMutation(id).unwrap();
      toast.success(currentStatus ? "🙈 Mission Hidden from Playground" : "👁️ Mission Shown in Playground");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteSubmit = async () => {
    if (!selectedMission?._id) return;
    try {
      await deleteMutation(selectedMission._id).unwrap();
      toast.success("🗑️ Mission deleted successfully");
      setDeleteDialogOpen(false);
    } catch (err) {
      toast.error("Failed to delete mission");
    }
  };

  // Table Configuration
  const columns = [
    { name: "ICON & TITLE", align: "left" },
    { name: "DESCRIPTION", align: "left" },
    { name: "TARGET PROGRESS", align: "center" },
    { name: "REWARD COINS", align: "center" },
    { name: "PEOPLE CLAIMED", align: "center" },
    { name: "VISIBILITY (HIDE/SHOW)", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const rows = rawMissions.map((item) => ({
    "ICON & TITLE": (
      <VuiBox display="flex" alignItems="center" gap={1.5}>
        <VuiBox p={1} sx={{ backgroundColor: "#0f172a", borderRadius: "10px", border: "1px solid #334155", color: "#38bdf8" }}>
          {item.icon === "Zap" ? <Bolt /> : item.icon === "Trophy" ? <EmojiEvents /> : <TrackChanges />}
        </VuiBox>
        <VuiBox>
          <VuiTypography variant="button" color="white" fontWeight="bold" display="block">
            {item.title}
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: "#94a3b8" }}>
            ID: {item.missionId}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    ),
    DESCRIPTION: (
      <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ maxWidth: "260px", display: "block" }}>
        {item.description}
      </VuiTypography>
    ),
    "TARGET PROGRESS": (
      <Chip
        label={`${item.target} Step Goal`}
        size="small"
        sx={{ backgroundColor: "rgba(6, 182, 212, 0.2)", color: "#38bdf8 !important", fontWeight: "bold", border: "1px solid #334155" }}
      />
    ),
    "REWARD COINS": (
      <VuiTypography variant="button" sx={{ color: "#fbbf24 !important" }} fontWeight="bold">
        🪙 +{item.rewardCoins} Coins
      </VuiTypography>
    ),
    "PEOPLE CLAIMED": (
      <Chip
        label={`🎁 ${item.totalClaimsCount || 0} People Claimed`}
        size="small"
        sx={{
          backgroundColor: "rgba(16, 185, 129, 0.15)",
          color: "#10b981 !important",
          fontWeight: "bold",
          border: "1px solid #10b981",
        }}
      />
    ),
    "VISIBILITY (HIDE/SHOW)": (
      <VuiBox display="flex" alignItems="center" justifyContent="center" gap={1}>
        <Switch
          checked={item.isActive}
          onChange={() => handleToggleStatus(item._id, item.isActive)}
          color="info"
        />
        <Chip
          label={item.isActive ? "SHOWING" : "HIDDEN"}
          size="small"
          sx={{
            backgroundColor: item.isActive ? "#10b981 !important" : "#f43f5e !important",
            color: "#ffffff !important",
            fontWeight: "bold",
            fontSize: "10px",
          }}
        />
      </VuiBox>
    ),
    ACTIONS: (
      <VuiBox display="flex" alignItems="center" justifyContent="center" gap={1}>
        <IconButton
          size="small"
          onClick={() => handleOpenEdit(item)}
          sx={{ color: "#38bdf8", backgroundColor: "#0f172a", border: "1px solid #334155" }}
        >
          <Edit fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => {
            setSelectedMission(item);
            setDeleteDialogOpen(true);
          }}
          sx={{ color: "#f43f5e", backgroundColor: "#0f172a", border: "1px solid #334155" }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </VuiBox>
    ),
  }));

  // Reusable TextField styling
  const customInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#0f172a !important",
      color: "#ffffff !important",
      borderRadius: "14px !important",
      "& fieldset": { borderColor: "#334155 !important" },
      "&:hover fieldset": { borderColor: "#f59e0b !important" },
      "&.Mui-focused fieldset": { borderColor: "#f59e0b !important", boxShadow: "0 0 14px rgba(245, 158, 11, 0.5)" },
    },
    "& .MuiInputBase-input": { color: "#ffffff !important", fontWeight: "bold" },
    "& .MuiInputBase-input::placeholder": { color: "#64748b !important", opacity: 1 },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header Bar */}
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <VuiBox>
            <VuiTypography variant="h4" color="white" fontWeight="bold">
              🎯 Daily Missions Management
            </VuiTypography>
            <VuiTypography variant="caption" color="text" fontWeight="medium">
              Create, edit, hide, and customize daily quests and Logic Coin rewards for the Playground Web App.
            </VuiTypography>
          </VuiBox>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#10b981 !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(16, 185, 129, 0.4)",
            }}
          >
            Create New Mission
          </Button>
        </VuiBox>

        {/* Stat Cards Header */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "TOTAL MISSIONS", fontWeight: "bold" }}
                count={`${totalMissions} Quests`}
                percentage={{ color: "info", text: "Configured Quests" }}
                icon={{ color: "info", component: <TrackChanges sx={{ color: "#fff", fontSize: 24 }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "ACTIVE IN PLAYGROUND", fontWeight: "bold" }}
                count={`${activeCount} Visible`}
                percentage={{ color: "success", text: "Live in Playground" }}
                icon={{ color: "success", component: <Visibility sx={{ color: "#fff", fontSize: 24 }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "HIDDEN MISSIONS", fontWeight: "bold" }}
                count={`${hiddenCount} Disabled`}
                percentage={{ color: "error", text: "Hidden Quests" }}
                icon={{ color: "error", component: <VisibilityOff sx={{ color: "#fff", fontSize: 24 }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "TOTAL QUEST CLAIMS", fontWeight: "bold" }}
                count={`${totalClaims} Claims`}
                percentage={{ color: "warning", text: "Quests Claimed by Players" }}
                icon={{ color: "warning", component: <MonetizationOn sx={{ color: "#fff", fontSize: 24 }} /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>

        {/* Missions Table */}
        <Card>
          <VuiBox p={3}>
            <VuiTypography variant="h5" color="white" fontWeight="bold">
              Configured Daily Missions & Hide/Show Status
            </VuiTypography>
          </VuiBox>
          <VuiBox p={2}>
            {isLoading || isFetching ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                <CircularProgress color="info" size={40} />
                <VuiTypography variant="button" color="text" fontWeight="bold">
                  Loading Missions...
                </VuiTypography>
              </VuiBox>
            ) : rows.length === 0 ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <VuiTypography variant="h3" mb={1}>🎯</VuiTypography>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                  No Daily Missions Configured
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Click "Create New Mission" to configure daily quests for players!
                </VuiTypography>
              </VuiBox>
            ) : (
              <Table columns={columns} rows={rows} />
            )}
          </VuiBox>
        </Card>

        <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            variant="outlined"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
              "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#06b6d4 !important", color: "#ffffff !important" },
            }}
          />
        </Card>

        {/* Create Mission Dialog Modal */}
        <Dialog
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ style: { backgroundColor: "#1e293b", color: "#ffffff", borderRadius: "20px", border: "1px solid #334155" } }}
        >
          <DialogTitle sx={{ color: "#ffffff", fontWeight: "bold", borderBottom: "1px solid #334155" }}>
            🎯 Create New Daily Mission
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <VuiBox display="flex" flexDirection="column" gap={2.5}>
              <VuiBox>
                <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                  Mission Title
                </VuiTypography>
                <TextField
                  fullWidth
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Novice Engineer"
                  variant="outlined"
                  sx={customInputSx}
                />
              </VuiBox>

              <VuiBox>
                <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                  Mission Description
                </VuiTypography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g. Complete your first 3 Classic Levels"
                  variant="outlined"
                  sx={customInputSx}
                />
              </VuiBox>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                    Target Goal (Number)
                  </VuiTypography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    placeholder="3"
                    variant="outlined"
                    sx={customInputSx}
                  />
                </Grid>

                <Grid item xs={6}>
                  <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                    Reward Coins (🪙)
                  </VuiTypography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.rewardCoins}
                    onChange={(e) => setFormData({ ...formData, rewardCoins: e.target.value })}
                    placeholder="10"
                    variant="outlined"
                    sx={customInputSx}
                  />
                </Grid>
              </Grid>

              <VuiBox display="flex" alignItems="center" justifyContent="space-between" p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  Visible in Playground App (Active)
                </VuiTypography>
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="info"
                />
              </VuiBox>
            </VuiBox>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: "1px solid #334155" }}>
            <Button onClick={() => setCreateModalOpen(false)} sx={{ color: "#94a3b8 !important" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSubmit}
              disabled={isCreating}
              sx={{ backgroundColor: "#10b981 !important", color: "#ffffff !important", fontWeight: "bold", borderRadius: "10px", px: 3 }}
            >
              Create Mission
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Mission Dialog Modal */}
        <Dialog
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ style: { backgroundColor: "#1e293b", color: "#ffffff", borderRadius: "20px", border: "1px solid #334155" } }}
        >
          <DialogTitle sx={{ color: "#38bdf8", fontWeight: "bold", borderBottom: "1px solid #334155" }}>
            ✏️ Edit Daily Mission
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <VuiBox display="flex" flexDirection="column" gap={2.5}>
              <VuiBox>
                <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                  Mission Title
                </VuiTypography>
                <TextField
                  fullWidth
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Novice Engineer"
                  variant="outlined"
                  sx={customInputSx}
                />
              </VuiBox>

              <VuiBox>
                <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                  Mission Description
                </VuiTypography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g. Complete your first 3 Classic Levels"
                  variant="outlined"
                  sx={customInputSx}
                />
              </VuiBox>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                    Target Goal (Number)
                  </VuiTypography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    placeholder="3"
                    variant="outlined"
                    sx={customInputSx}
                  />
                </Grid>

                <Grid item xs={6}>
                  <VuiTypography variant="button" color="text" fontWeight="bold" mb={0.8} display="block">
                    Reward Coins (🪙)
                  </VuiTypography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.rewardCoins}
                    onChange={(e) => setFormData({ ...formData, rewardCoins: e.target.value })}
                    placeholder="10"
                    variant="outlined"
                    sx={customInputSx}
                  />
                </Grid>
              </Grid>

              <VuiBox display="flex" alignItems="center" justifyContent="space-between" p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  Visible in Playground App (Active)
                </VuiTypography>
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="info"
                />
              </VuiBox>
            </VuiBox>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: "1px solid #334155" }}>
            <Button onClick={() => setEditModalOpen(false)} sx={{ color: "#94a3b8 !important" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditSubmit}
              disabled={isUpdating}
              sx={{ backgroundColor: "#06b6d4 !important", color: "#ffffff !important", fontWeight: "bold", borderRadius: "10px", px: 3 }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ style: { backgroundColor: "#0f172a", color: "#ffffff", borderRadius: "20px", border: "1px solid #f43f5e" } }}
        >
          <DialogTitle sx={{ color: "#f43f5e", fontWeight: "bold" }}>
            🗑️ Delete Mission
          </DialogTitle>
          <DialogContent>
            <VuiTypography variant="body2" color="text">
              Are you sure you want to delete the mission <strong style={{ color: "#fff" }}>{selectedMission?.title}</strong>? This action cannot be undone.
            </VuiTypography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#94a3b8 !important" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteSubmit}
              sx={{ backgroundColor: "#f43f5e !important", color: "#ffffff !important", fontWeight: "bold", borderRadius: "10px" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MissionsManagement;
